import * as React from "react"
import { router } from "@inertiajs/react"
import { Check, ChevronDown, ChevronUp, Plus, X } from "lucide-react"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { PhotoLibraryDialog } from "@/components/photo-library/PhotoLibraryDialog"
import { cn } from "@/lib/utils"
import type { LibraryPhoto, PhotoRef } from "./types"

/** Choose-one mode: several photos stay placed, one is shown. `null`
 *  selects the plain default, which the picker offers as its own tile. */
export type ChooseOne = { selectedPhotoId: number | null; plainLabel: string }

export function PhotoPicker({
  slotKey,
  siteId,
  photos,
  library,
  maxItems,
  chooseOne,
}: {
  slotKey: string
  siteId: number | null
  photos: PhotoRef[]
  library: LibraryPhoto[]
  maxItems?: number
  chooseOne?: ChooseOne
}) {
  const [current, setCurrent] = React.useState<PhotoRef[]>(photos)
  const [selectedId, setSelectedId] = React.useState<number | null>(chooseOne?.selectedPhotoId ?? null)
  const [pickerOpen, setPickerOpen] = React.useState(false)

  function persist(next: PhotoRef[]) {
    setCurrent(next)
    // Removing the shown photo reverts to the plain default (the server does
    // the same), so the tiles never claim a photo that is no longer placed.
    if (selectedId !== null && !next.some((p) => p.id === selectedId)) setSelectedId(null)
    router.patch(
      `/admin/pages/placements/${encodeURIComponent(slotKey)}`,
      { site_id: siteId, photo_ids: next.map((p) => p.id) },
      { preserveScroll: true, preserveState: true },
    )
  }

  function select(photoId: number | null) {
    if (!chooseOne || photoId === selectedId) return
    setSelectedId(photoId)
    router.patch(
      `/admin/pages/placements/${encodeURIComponent(slotKey)}/selection`,
      { site_id: siteId, photo_id: photoId },
      { preserveScroll: true, preserveState: true },
    )
  }

  function addPhoto(photo: LibraryPhoto) {
    const ref: PhotoRef = { id: photo.id, url: photo.url, alt_text: photo.alt_text_en }
    const next = maxItems && current.length >= maxItems ? [ ref ] : [ ...current, ref ]
    persist(next)
  }

  // A whole batch lands in one PATCH — the slot is saved as a list, so ten
  // photos cost the same round trip as one.
  function addPhotos(photos: LibraryPhoto[]) {
    const placed = new Set(current.map((p) => p.id))
    const refs = photos
      .filter((photo) => !placed.has(photo.id))
      .map((photo) => ({ id: photo.id, url: photo.url, alt_text: photo.alt_text_en }))
    if (refs.length === 0) return
    persist([ ...current, ...refs ])
  }

  function removePhoto(id: number) {
    persist(current.filter((p) => p.id !== id))
  }

  function move(index: number, delta: number) {
    const target = index + delta
    if (target < 0 || target >= current.length) return
    const next = [ ...current ]
    ;[ next[index], next[target] ] = [ next[target], next[index] ]
    persist(next)
  }

  const atMax = maxItems !== undefined && current.length >= maxItems
  // Single-photo slots (a card image, a chosen background) still swap one for
  // one; a list slot — a room's gallery, a slideshow — takes a batch.
  const multiSelect = maxItems === undefined

  // The live choice reads at a glance: an accent ring and a "Showing" badge on
  // the tile in use; every other tile is a button that makes it the choice.
  function tileClass(isSelected: boolean) {
    return cn(
      "overflow-hidden rounded-lg border",
      chooseOne && (isSelected ? "border-accent ring-2 ring-accent" : "border-hairline hover:border-accent"),
      !chooseOne && "border-hairline",
    )
  }

  function showingBadge() {
    return (
      <Badge tone="solid" className="absolute left-2 top-2 gap-1 shadow">
        <Check className="h-3 w-3" />
        Showing
      </Badge>
    )
  }

  return (
    <div className="space-y-3">
      {(current.length > 0 || chooseOne) && (
        <div className="grid gap-3 sm:grid-cols-3 lg:grid-cols-4">
          {chooseOne && (
            <button
              type="button"
              onClick={() => select(null)}
              aria-pressed={selectedId === null}
              className={cn(tileClass(selectedId === null), "text-left")}
            >
              <div className="relative flex aspect-video w-full items-center justify-center bg-teal">
                {selectedId === null && showingBadge()}
                <span className="text-sm font-medium text-white">{chooseOne.plainLabel}</span>
              </div>
              <div className="p-1.5 text-xs text-ink-muted">
                {selectedId === null ? "On the live page" : "Click to show"}
              </div>
            </button>
          )}

          {current.map((photo, index) => {
            const isSelected = chooseOne !== undefined && selectedId === photo.id
            const preview = photo.url ? (
              <img src={photo.url} alt={photo.alt_text ?? ""} className="aspect-video w-full object-cover" />
            ) : (
              <div className="flex aspect-video items-center justify-center bg-surface text-xs text-ink-muted">
                No preview
              </div>
            )

            return (
              <div key={photo.id} className={tileClass(isSelected)}>
                {chooseOne ? (
                  <button
                    type="button"
                    onClick={() => select(photo.id)}
                    aria-pressed={isSelected}
                    aria-label={isSelected ? "Showing this photo" : "Show this photo"}
                    className="relative block w-full"
                  >
                    {preview}
                    {isSelected && showingBadge()}
                  </button>
                ) : (
                  preview
                )}
                <div className="flex items-center justify-between gap-1 p-1.5">
                  {chooseOne ? (
                    <span className="truncate text-xs text-ink-muted">
                      {isSelected ? "On the live page" : "Click to show"}
                    </span>
                  ) : (
                    <div className="flex gap-1">
                      <button
                        type="button"
                        onClick={() => move(index, -1)}
                        disabled={index === 0}
                        className="rounded p-1 text-ink-muted hover:bg-surface hover:text-ink-display disabled:opacity-30"
                        aria-label="Move earlier"
                      >
                        <ChevronUp className="h-3.5 w-3.5" />
                      </button>
                      <button
                        type="button"
                        onClick={() => move(index, 1)}
                        disabled={index === current.length - 1}
                        className="rounded p-1 text-ink-muted hover:bg-surface hover:text-ink-display disabled:opacity-30"
                        aria-label="Move later"
                      >
                        <ChevronDown className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  )}
                  <button
                    type="button"
                    onClick={() => removePhoto(photo.id)}
                    className="rounded p-1 text-ink-muted hover:bg-danger-faded hover:text-danger-display"
                    aria-label="Remove"
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                </div>
              </div>
            )
          })}
        </div>
      )}

      {!atMax && (
        <Button type="button" variant="secondary" size="sm" onClick={() => setPickerOpen(true)}>
          <Plus className="mr-1.5 h-3.5 w-3.5" />
          {multiSelect ? "Add photos" : "Add photo"}
        </Button>
      )}

      <PhotoLibraryDialog
        open={pickerOpen}
        onOpenChange={setPickerOpen}
        library={library}
        onSelect={addPhoto}
        multiple={multiSelect}
        onSelectMany={addPhotos}
        placedIds={current.map((p) => p.id)}
      />
    </div>
  )
}
